import React, { useEffect, useMemo, useRef, useState } from 'react';

export const AnimationWrapper = ({
  show,
  children,
  from = { opacity: 0 },
  to = { opacity: 1 },
  unMountAnimation,
  options = { duration: 500, fill: 'forwards' },
  transformOrigin,
  throttle = 200,
  ...props
}) => {
  const elementRef = useRef(null);

  const [removeState, setRemove] = useState(!show);

  const timeout = useRef(null);

  const [throttledShow, setThrottledShow] = useState(show);

  const childrenWithProps = useMemo(
    () =>
      React.Children.map(children, child => {
        // Checking isValidElement is the safe way and avoids a
        // typescript error too.
        if (React.isValidElement(child)) {
          return React.cloneElement(child, props);
        }
        return child;
      }),
    [props, children]
  );

  useEffect(() => {
    if (timeout.current !== null) {
      clearTimeout(timeout.current);
      if (show !== throttledShow) {
        timeout.current = setTimeout(() => {
          setThrottledShow(show);
          timeout.current = null;
        }, throttle);
      }
    } else {
      if (show !== throttledShow) {
        timeout.current = setTimeout(() => {
          setThrottledShow(show);
          timeout.current = null;
        }, throttle);
      }
    }
  }, [show]);

  useEffect(() => {
    const childElement = elementRef.current;
    if (throttledShow) {
      setRemove(false);
      if (!childElement) return;
      childElement.animate([from, to], options);
    } else {
      if (!childElement) return;
      const animation = childElement.animate(unMountAnimation ?? [to, from], options);
      animation.onfinish = () => {
        setRemove(true);
      };
    }
  }, [throttledShow, removeState]);

  if (removeState) {
    return null;
  }

  return (
    <div style={{ transformOrigin, zIndex: '100' }} ref={elementRef}>
      {childrenWithProps}
    </div>
  );
};
